import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CoreDatePicker, CoreMenuItem, CoreSelect, CoreTimePicker } from "@verity/components/core";
import { ChangeEventHandler, Dispatch, SetStateAction, useContext } from "react";
import apiClient from "../../../../../helpers/api-client";
import i18n from "../../../../../i18n";
import { PortalControllerContext } from "../../../../context";
import { DURATION_OPTIONS } from "../../constants";
import ContentWrapper from "../components/ContentWrapper";
import { FormMultiAutoComplete } from "../components/FormMultiAutoComplete";
import FormTextField from "../components/FormTextField";
import { InterviewDetailsErrorsType, InterviewDetailsType } from "../types";
import styles from "./Form.module.css";

type InterviewDetailsFormProps = {
	formValues: InterviewDetailsType;
	setFormValues: Dispatch<SetStateAction<InterviewDetailsType>>;
	errors: InterviewDetailsErrorsType;
	setErrors: Dispatch<SetStateAction<InterviewDetailsErrorsType>>;
};

type InterviewerType = Awaited<ReturnType<typeof apiClient.liveClient.getCourseLiveProctorInfo>>[number];

export default function InterviewDetailsForm({
	formValues,
	setFormValues,
	errors,
	setErrors,
}: InterviewDetailsFormProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteId, lmsProvidedCourseId } = portalContext;

	const { data: interviewers, error } = useQuery<InterviewerType[]>({
		queryKey: [instituteId],
		queryFn: () => apiClient.liveClient.getCourseLiveProctorInfo(lmsProvidedCourseId!),
		enabled: !!instituteId || !!lmsProvidedCourseId,
	});

	if (error) {
		console.error("Error fetching user emails:", error);
	}

	function clearError<K extends keyof InterviewDetailsType>(key: K) {
		setErrors((prev) => ({ ...prev, [key]: "" }));
	}

	function changeFactory<K extends keyof InterviewDetailsType>(
		key: K,
	): ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> {
		return (e) => {
			setFormValues((prev) => ({
				...prev,
				[key]: e.target.value,
			}));
			clearError(key);
		};
	}

	// INTERVIEWER ID HANDLER
	function displayInterviewers(uuids: string[] | null): string[] {
		if (!interviewers || !uuids) return [];
		return uuids.map((uuid) => {
			const interviewer = interviewers.find((i) => i.uuid === uuid);
			if (!interviewer) return "Unknown Interviewer";
			return `${interviewer.name}: ${interviewer.email}`;
		});
	}

	function getInterviewerIds(values: string[]): string[] {
		if (!interviewers || !values) return [];
		return values
			.map((value) => {
				const email = value.split(":")[1].trim();
				const interviewer = interviewers.find((i) => i.email === email);
				if (interviewer) return interviewer.uuid;
			})
			.filter((uuid) => uuid !== undefined);
	}
	//

	function isValidDate(date: Date) {
		return !Number.isNaN(date.getTime());
	}

	// START TIME HANDLER
	function setStartDate(prev: Date, date: Date | null): Date {
		clearError("startTime");
		if (!date || !isValidDate(date)) return prev;

		return new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			prev.getHours(),
			prev.getMinutes(),
			prev.getSeconds(),
			prev.getMilliseconds(),
		);
	}

	function setStartTime(prev: Date, time: Date): Date {
		clearError("startTime");

		return new Date(
			prev.getFullYear(),
			prev.getMonth(),
			prev.getDate(),
			time.getHours(),
			time.getMinutes(),
			time.getSeconds(),
			time.getMilliseconds(),
		);
	}
	//

	// END DATE HANDLER
	function getDuration(start: Date, end: Date) {
		return end.getTime() - start.getTime();
	}

	function setEndTime(start: Date, duration: number) {
		clearError("endTime");

		return new Date(start.getTime() + duration);
	}
	//

	return (
		<ContentWrapper
			title={i18n.t(`candidate.scheduleInterview.detailsStep.title`)}
			description={i18n.t(`candidate.scheduleInterview.detailsStep.description`)}
		>
			<div className={styles.form}>
				<FormTextField
					title={i18n.t(`candidate.scheduleInterview.detailsStep.fields.interviewTitle.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.detailsStep.fields.interviewTitle.placeholder`)}
					value={formValues.title}
					handleChange={changeFactory("title")}
					maxLength={100}
					error={errors.title}
				/>
				<FormTextField
					title={i18n.t(`candidate.scheduleInterview.detailsStep.fields.candidateName.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.detailsStep.fields.candidateName.placeholder`)}
					value={formValues.candidateName}
					handleChange={changeFactory("candidateName")}
					error={errors.candidateName}
				/>
				<FormTextField
					title={i18n.t(`candidate.scheduleInterview.detailsStep.fields.candidateEmail.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.detailsStep.fields.candidateEmail.placeholder`)}
					value={formValues.candidateEmail}
					handleChange={changeFactory("candidateEmail")}
					error={errors.candidateEmail}
				/>
				<FormTextField
					title={i18n.t(`candidate.scheduleInterview.detailsStep.fields.positionName.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.detailsStep.fields.positionName.placeholder`)}
					value={formValues.positionName}
					handleChange={changeFactory("positionName")}
					error={errors.positionName}
				/>
				<FormMultiAutoComplete
					className={styles.freeHeight}
					title={i18n.t(`candidate.scheduleInterview.detailsStep.fields.interviewerEmail.label`)}
					placeholder={i18n.t(`candidate.scheduleInterview.detailsStep.fields.interviewerEmail.placeholder`)}
					value={displayInterviewers(formValues.interviewerUuids)}
					onChange={(_, value) => {
						setFormValues((prev) => ({
							...prev,
							interviewerUuids: getInterviewerIds(value),
						}));
						clearError("interviewerUuids");
					}}
					options={interviewers ? displayInterviewers(interviewers.map((i) => i.uuid)) || [""] : [""]}
					error={errors.interviewerUuids}
				/>
				<>
					<Typography>{i18n.t(`candidate.scheduleInterview.detailsStep.fields.date.label`)}</Typography>
					<CoreDatePicker
						disablePast
						value={formValues.startTime}
						onChange={(date) => {
							setFormValues((prev) => {
								const start = setStartDate(prev.startTime, date);
								return {
									...prev,
									startTime: start,
									endTime: setEndTime(start, getDuration(prev.startTime, prev.endTime)),
								};
							});
						}}
						error={errors.startTime}
					/>
				</>
				<>
					<Typography>{i18n.t(`candidate.scheduleInterview.detailsStep.fields.time.label`)}</Typography>
					<CoreTimePicker
						value={formValues.startTime}
						onChange={(time) => {
							setFormValues((prev) => {
								const start = setStartTime(prev.startTime, time);
								return {
									...prev,
									startTime: start,
									endTime: setEndTime(start, getDuration(prev.startTime, prev.endTime)),
								};
							});
						}}
						error={errors.startTime}
					/>
				</>
				<>
					<Typography>{i18n.t(`candidate.scheduleInterview.detailsStep.fields.duration.label`)}</Typography>
					<CoreSelect
						value={getDuration(formValues.startTime, formValues.endTime)}
						onChange={(e) => {
							setFormValues((prev) => ({
								...prev,
								endTime: setEndTime(prev.startTime, Number(e.target.value)),
							}));
						}}
					>
						{DURATION_OPTIONS.map((duration, idx) => (
							<CoreMenuItem key={idx} value={duration.value}>
								{duration.label}
							</CoreMenuItem>
						))}
					</CoreSelect>
				</>
			</div>
		</ContentWrapper>
	);
}
